import { normalizePhone } from '$func/phone'
import { dateWithOffset } from '$logic/client-device/time-offset.service'

const STORAGE_PREFIX = 'auth.throttle.v1'

export type ThrottleEndpoint = 'signin' | 'resend' | 'verify'

export type ThrottleDetails = {
  retryAfter: number
  scope: string
  limit?: number
  windowSeconds?: number
}

export type ThrottleBlock = ThrottleDetails & {
  retryUntil: number
}

export type ThrottleFailure = { throttle: ThrottleDetails }

const SHARED_SCOPES = ['ip', 'user', 'session', 'token', 'unknown']

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object'

/**
 * Reads rate-limit data from both action errors and HTTP 429 responses. Older
 * auth deployments may omit optional metadata or serialize `message` as JSON,
 * so only the server cooldown itself is required.
 */
export function getThrottleDetails(
  error: unknown,
  fallbackRetryAfter?: unknown,
): ThrottleDetails | null {
  if (!isRecord(error) || error.code !== 'TOO_MANY_REQUESTS') return null

  const details =
    parseDetails(error.message) ?? parseDetails(error.details) ?? error
  const retryAfter =
    toPositiveFiniteNumber(details.retryAfter) ??
    parseRetryAfter(fallbackRetryAfter)
  if (!retryAfter) return null

  const scope =
    typeof details.scope === 'string' && details.scope
      ? details.scope
      : 'unknown'
  const limit = toPositiveFiniteNumber(details.limit)
  const windowSeconds = toPositiveFiniteNumber(details.windowSeconds)

  return {
    retryAfter,
    scope,
    ...(limit ? { limit } : {}),
    ...(windowSeconds ? { windowSeconds } : {}),
  }
}

export const isThrottleFailure = (value: unknown): value is ThrottleFailure =>
  isRecord(value) && isThrottleDetails(value.throttle)

/**
 * Stores only the server-provided deadline to avoid repeat requests after a
 * throttle response. The backend remains the authority: this is strictly UI.
 */
export function persistThrottleBlock(
  endpoint: ThrottleEndpoint,
  phone: string,
  details: ThrottleDetails,
): ThrottleBlock {
  const block: ThrottleBlock = {
    ...details,
    retryUntil: dateWithOffset() + details.retryAfter * 1000,
  }

  try {
    localStorage.setItem(
      getStorageKey(endpoint, details.scope, phone),
      JSON.stringify(block),
    )
  } catch {
    // Storage can be unavailable in private browsing. The in-memory UI still
    // observes the cooldown for this visit.
  }

  return block
}

/** Returns the longest active block that applies to this endpoint and phone. */
export function getThrottleBlock(
  endpoint: ThrottleEndpoint,
  phone: string,
): ThrottleBlock | null {
  const scopes = phone ? ['phone', ...SHARED_SCOPES] : SHARED_SCOPES
  const blocks = scopes
    .map(scope => getStoredBlock(getStorageKey(endpoint, scope, phone)))
    .filter((block): block is ThrottleBlock => block !== null)

  return blocks.reduce<ThrottleBlock | null>(
    (longest, block) =>
      !longest || block.retryUntil > longest.retryUntil ? block : longest,
    null,
  )
}

function getStorageKey(
  endpoint: ThrottleEndpoint,
  scope: string,
  phone: string,
): string {
  const tracker = scope === 'phone' ? normalizePhone(phone) : 'shared'
  return `${STORAGE_PREFIX}.${endpoint}.${scope}.${tracker}`
}

function getStoredBlock(key: string): ThrottleBlock | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null

    const parsed: unknown = JSON.parse(raw)
    if (!isThrottleBlock(parsed) || parsed.retryUntil <= dateWithOffset()) {
      localStorage.removeItem(key)
      return null
    }

    return parsed
  } catch {
    return null
  }
}

function isThrottleBlock(value: unknown): value is ThrottleBlock {
  return (
    isRecord(value) &&
    isPositiveFiniteNumber(value.retryUntil) &&
    isThrottleDetails(value)
  )
}

function isThrottleDetails(value: unknown): value is ThrottleDetails {
  return (
    isRecord(value) &&
    typeof value.scope === 'string' &&
    Boolean(value.scope) &&
    isPositiveFiniteNumber(value.retryAfter) &&
    (value.limit === undefined || isPositiveFiniteNumber(value.limit)) &&
    (value.windowSeconds === undefined ||
      isPositiveFiniteNumber(value.windowSeconds))
  )
}

function parseDetails(value: unknown): Record<string, unknown> | null {
  if (isRecord(value)) return value
  if (typeof value !== 'string') return null

  try {
    const parsed: unknown = JSON.parse(value)
    return isRecord(parsed) ? parsed : null
  } catch {
    return null
  }
}

function parseRetryAfter(value: unknown): number | null {
  const seconds = toPositiveFiniteNumber(value)
  if (seconds) return Math.ceil(seconds)
  if (typeof value !== 'string') return null

  const retryAt = Date.parse(value)
  if (!Number.isFinite(retryAt)) return null
  return Math.max(1, Math.ceil((retryAt - Date.now()) / 1000))
}

function toPositiveFiniteNumber(value: unknown): number | null {
  const parsed =
    typeof value === 'string' && value.trim() ? Number(value) : value
  return isPositiveFiniteNumber(parsed) ? parsed : null
}

function isPositiveFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}
